import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import io from "socket.io-client";
import { API_BASE_URL, SOCKET_URL } from "../config";

const BidDialog = ({ trikot, open, onClose, onBidSuccess }) => {
  const { user } = useAuth();
  const [bidAmountInput, setBidAmountInput] = useState(String(trikot.currentBid + 5));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [socket, setSocket] = useState(null);

  // Initialize socket when the component mounts and user is authenticated
  useEffect(() => {
    if (user) {
      const newSocket = io(SOCKET_URL);
      setSocket(newSocket);

      return () => {
        if (newSocket) {
          newSocket.disconnect();
        }
      };
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      onClose();
    }
  }, [user, onClose]);

  useEffect(() => {
    const checkExpiration = () => {
      if (new Date(trikot.endTime) < new Date()) {
        setError("Diese Auktion ist bereits beendet.");
        setTimeout(() => {
          onClose();
        }, 2000); // Close dialog after 2 seconds
      }
    };

    checkExpiration();
    const timer = setInterval(checkExpiration, 1000); // Check every second

    return () => clearInterval(timer);
  }, [trikot.endTime, onClose]);

  const handleBidSubmit = async () => {
    if (new Date(trikot.endTime) < new Date()) {
      setError("Diese Auktion ist bereits beendet.");
      setTimeout(() => {
        onClose();
      }, 2000);
      return;
    }

    const numericAmount = Number(bidAmountInput) || 0;
    if (numericAmount < trikot.currentBid + 5) {
      setError("Das Gebot muss mindestens 5€ über dem aktuellen Gebot liegen.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const numericAmount = Number(bidAmountInput) || 0;
      const response = await axios.post(
        `${API_BASE_URL}/api/bids`,
        {
          trikotId: trikot._id,
          amount: numericAmount,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Handle the response which now includes the possibly extended endTime
      if (socket) {
        socket.emit("newBid", {
          trikotId: trikot._id,
          amount: numericAmount,
          userId: user._id,
          endTime: response.data.endTime, // Include new end time
        });
      }

      // Call onBidSuccess with the updated bid data including endTime
      if (onBidSuccess) {
        onBidSuccess({
          trikotId: trikot._id,
          amount: numericAmount,
          endTime: response.data.endTime, // Include new end time
        });
      }

      onClose();
    } catch (error) {
      console.error("Bid error:", error);
      setError(
        error.response?.data?.message ||
          "Fehler beim Abgeben des Gebots. Bitte versuchen Sie es später erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  // Update bidAmountInput when trikot changes
  useEffect(() => {
    if (trikot) {
      const newAmount = trikot.currentBid + 5;
      setBidAmountInput(String(newAmount));
    }
  }, [trikot]);

  const handleBidAmountChange = (e) => {
    const value = e.target.value;
    // Erlaube leeres Feld oder Zahlen
    if (value === "" || /^\d+$/.test(value)) {
      setBidAmountInput(value);
    }
  };

  // Hilfsfunktion zum Erhöhen des Gebots
  const increaseBid = () => {
    // Explizit prüfen, ob das Feld leer ist (nicht auf Falsy-Wert von Number("0") vertrauen)
    const currentAmount = bidAmountInput === "" 
      ? trikot.currentBid + 5 
      : Number(bidAmountInput);
    const newAmount = currentAmount + 5;
    setBidAmountInput(String(newAmount));
    setError(""); // Fehler zurücksetzen
  };

  // Hilfsfunktion zum Verringern des Gebots
  const decreaseBid = () => {
    // Explizit prüfen, ob das Feld leer ist (nicht auf Falsy-Wert von Number("0") vertrauen)
    const currentAmount = bidAmountInput === "" 
      ? trikot.currentBid + 5 
      : Number(bidAmountInput);
    const minAmount = trikot.currentBid + 5;
    const newAmount = Math.max(minAmount, currentAmount - 5);
    setBidAmountInput(String(newAmount));
    setError(""); // Fehler zurücksetzen
  };

  const handleKeyDown = (e) => {
    // Pfeil hoch: +5€
    if (e.key === "ArrowUp") {
      e.preventDefault();
      increaseBid();
    }
    // Pfeil runter: -5€ (aber nicht unter Mindestgebot)
    else if (e.key === "ArrowDown") {
      e.preventDefault();
      decreaseBid();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        elevation: 24,
        sx: {
          backgroundColor: "rgb(0, 17, 40)",
          backgroundImage: "linear-gradient(150deg, rgb(19, 36, 78) 0%, rgb(0, 0, 0) 100%)",
          backgroundBlendMode: "normal",
          border: "2px solid rgba(112, 197, 255, 0.3)",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.8), 0 0 40px rgba(112, 197, 255, 0.2), inset 0 0 20px rgba(112, 197, 255, 0.1)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <DialogTitle sx={{ color: "#ffffff", fontFamily: "countach, authority-italic, sans-serif" }}>
        Gebot abgeben für #{trikot.number} {trikot.playerName}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" sx={{ mb: 2, color: "#ffffff" }}>
          Aktuelles Gebot: {formatCurrency(trikot.currentBid)}
        </Typography>

        <Typography variant="body2" sx={{ mb: 2, color: "#ffffff" }}>
          Mindestgebot: {formatCurrency(trikot.currentBid + 5)}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Ihr Gebot (€)"
            type="text"
            fullWidth
            value={bidAmountInput}
            onChange={handleBidAmountChange}
            onKeyDown={handleKeyDown}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            disabled={loading}
            helperText={`Mindestens ${formatCurrency(
              trikot.currentBid + 5
            )} (5€ über aktuellem Gebot). Pfeiltasten ↑↓ für ±5€`}
            sx={{
              "& .MuiInputLabel-root": {
                color: "#ffffff",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#70c5ff",
              },
              "& .MuiOutlinedInput-root": {
                color: "#ffffff",
                "& fieldset": {
                  borderColor: "#ffffff",
                },
                "&:hover fieldset": {
                  borderColor: "#70c5ff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#70c5ff",
                },
              },
              "& .MuiFormHelperText-root": {
                color: "rgba(255, 255, 255, 0.7)",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              mt: "8px", // Ausrichtung mit dem TextField (margin="dense" hat 8px margin-top)
              gap: 0.5,
            }}
          >
            <IconButton
              onClick={increaseBid}
              disabled={loading}
              size="small"
              sx={{
                color: "#70c5ff",
                border: "1px solid rgba(112, 197, 255, 0.3)",
                borderRadius: "4px 4px 0 0",
                minWidth: "40px",
                height: "28px",
                "&:hover": {
                  backgroundColor: "rgba(112, 197, 255, 0.1)",
                  borderColor: "#70c5ff",
                },
                "&:disabled": {
                  color: "rgba(255, 255, 255, 0.3)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
              aria-label="Gebot um 5€ erhöhen"
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={decreaseBid}
              disabled={loading}
              size="small"
              sx={{
                color: "#70c5ff",
                border: "1px solid rgba(112, 197, 255, 0.3)",
                borderRadius: "0 0 4px 4px",
                minWidth: "40px",
                height: "28px",
                "&:hover": {
                  backgroundColor: "rgba(112, 197, 255, 0.1)",
                  borderColor: "#70c5ff",
                },
                "&:disabled": {
                  color: "rgba(255, 255, 255, 0.3)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
              aria-label="Gebot um 5€ verringern"
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ color: "#ffffff" }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={handleBidSubmit}
          variant="contained"
          color="secondary"
          disabled={loading || (bidAmountInput === "" || Number(bidAmountInput) < trikot.currentBid + 5) || !user}
          sx={{
            color: "#00182c",
            fontWeight: "bold",
          }}
        >
          {loading ? "Wird verarbeitet..." : "Gebot abgeben"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BidDialog;
